import { Link } from "react-router-dom";
import ReliabilityBadge from "./ReliabilityBadge";
import { formatHeat, type Article } from "@/lib/articles";

type ArticleCardProps = {
  article: Article;
  layout?: "vertical" | "horizontal" | "text";
};

export default function ArticleCard({ article, layout = "vertical" }: ArticleCardProps) {
  const meta = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] md:text-xs text-foreground-500 font-label">
      <span className="inline-flex items-center gap-1">
        <i className="ri-user-3-line" aria-hidden="true" />
        {article.author}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-calendar-line" aria-hidden="true" />
        {article.publishedAt}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-fire-line text-primary-500" aria-hidden="true" />
        热度 {formatHeat(article.heat)}
      </span>
    </div>
  );

  if (layout === "text") {
    return (
      <article className="group">
        <Link to={`/article/${article.slug}`} className="block cursor-pointer">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <ReliabilityBadge value={article.reliability} />
            <span className="text-[11px] font-label font-bold text-secondary-700 bg-secondary-100 rounded-full px-2 py-0.5">
              {article.topicName}
            </span>
          </div>
          <h3 className="font-heading text-base md:text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <div className="mt-2.5">{meta}</div>
        </Link>
      </article>
    );
  }

  if (layout === "horizontal") {
    return (
      <article className="group">
        <Link
          to={`/article/${article.slug}`}
          className="flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50/60"
        >
          <div className="relative w-28 h-20 sm:w-36 sm:h-24 flex-shrink-0 overflow-hidden rounded-md">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 独家大瓜`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <div className="min-w-0 flex flex-col">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <ReliabilityBadge value={article.reliability} />
            </div>
            <h3 className="font-heading text-sm md:text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
              {article.title}
            </h3>
            <div className="mt-auto pt-2">{meta}</div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.slug}`} className="block cursor-pointer">
        <div className="relative w-full h-48 md:h-52 overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 独家大瓜`}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute top-3 left-3">
            <ReliabilityBadge value={article.reliability} />
          </div>
          <span className="absolute top-3 right-3 rounded-full bg-foreground-950/75 px-2.5 py-0.5 text-[11px] font-label font-bold text-background-50 backdrop-blur-sm">
            {article.topicName}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-4 md:p-5">
          <h3 className="font-heading text-base md:text-[17px] font-bold leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-2.5 text-xs md:text-[13px] leading-relaxed text-foreground-600 line-clamp-3">
            {article.summary}
          </p>
          <div className="mt-4 pt-3 border-t border-background-200">{meta}</div>
        </div>
      </Link>
    </article>
  );
}